import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { Page } from '#components/Page';
import { BudgetFileSelection } from '#components/manager/BudgetFileSelection';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useNavigate } from '#hooks/useNavigate';
import { PRODUCT_NAME } from '#product-brand';

import { CloudApiError } from './api-client';
import { CLOUD_NAMESPACE, CloudI18nProvider } from './cloud-i18n';
import { CloudBackupsCard } from './CloudBackupsCard';
import { CloudBudgetBindingCard } from './CloudBudgetBindingCard';
import { CloudSessionProvider, useCloudSession } from './CloudSessionProvider';
import { useCloudBudgetBinding } from './useCloudBudgetBinding';
import { useCloudWorkspaces } from './useCloudWorkspaces';

export function LocalBudgetEntryRoute() {
  return (
    <CloudI18nProvider>
      <LocalBudgetEntryPage />
    </CloudI18nProvider>
  );
}

function LocalBudgetEntryPage() {
  const [localBudgetId] = useMetadataPref('id');
  const navigate = useNavigate();
  const { t } = useTranslation(CLOUD_NAMESPACE);

  useEffect(() => {
    document.title = PRODUCT_NAME;
  }, []);

  useEffect(() => {
    if (localBudgetId) {
      void navigate('/product-finance', { replace: true });
    }
  }, [localBudgetId, navigate]);

  return (
    <Page header={PRODUCT_NAME} padding={12}>
      <main
        aria-label={t('Local budgets')}
        style={{ width: '100%', maxWidth: 760, margin: '0 auto' }}
      >
        <LocalBudgetEntry />
      </main>
    </Page>
  );
}

export function CloudConnectionRoute({
  isEntryFlow = false,
}: Readonly<{ isEntryFlow?: boolean }>) {
  return (
    <CloudI18nProvider>
      <CloudSessionProvider callbackPath={isEntryFlow ? '/entry' : '/cloud'}>
        <CloudConnectionWithBudget isEntryFlow={isEntryFlow} />
      </CloudSessionProvider>
    </CloudI18nProvider>
  );
}

function CloudConnectionWithBudget({
  isEntryFlow,
}: Readonly<{ isEntryFlow: boolean }>) {
  const [localBudgetId] = useMetadataPref('id');
  const [localBudgetName] = useMetadataPref('budgetName');
  const [encryptKeyId] = useMetadataPref('encryptKeyId');
  return (
    <CloudConnectionPage
      isEntryFlow={isEntryFlow}
      isEndToEndEncrypted={Boolean(encryptKeyId)}
      localBudgetId={localBudgetId ?? null}
      localBudgetName={localBudgetName ?? null}
    />
  );
}

export function CloudConnectionPage({
  isEntryFlow = false,
  isEndToEndEncrypted = false,
  localBudgetId = null,
  localBudgetName = null,
}: Readonly<{
  isEntryFlow?: boolean;
  isEndToEndEncrypted?: boolean;
  localBudgetId?: string | null;
  localBudgetName?: string | null;
}>) {
  const session = useCloudSession();
  const { t } = useTranslation(CLOUD_NAMESPACE);
  const navigate = useNavigate();
  const canSelectLocalBudget = isEntryFlow && localBudgetId === null;

  useEffect(() => {
    document.title = isEntryFlow
      ? PRODUCT_NAME
      : `${t('Cloud account')} | ${PRODUCT_NAME}`;
  }, [isEntryFlow, t]);

  useEffect(() => {
    if (isEntryFlow && localBudgetId !== null) {
      void navigate('/product-finance', { replace: true });
    }
  }, [isEntryFlow, localBudgetId, navigate]);

  return (
    <Page
      header={
        isEntryFlow ? (
          PRODUCT_NAME
        ) : (
          <Trans ns={CLOUD_NAMESPACE}>Cloud account</Trans>
        )
      }
      padding={12}
      style={{ color: theme.pageText }}
    >
      <main
        aria-label={t('Cloud account')}
        style={{ width: '100%', maxWidth: 760, margin: '0 auto' }}
      >
        {session.status === 'disabled' && (
          <MessageCard title={t('Cloud features are not configured')}>
            <p>
              {t(
                'The SaaS connection is unavailable until all public environment variables are set.',
              )}
            </p>
            <LocalFirstNotice />
            {localBudgetId !== null && <ContinueLocallyButton />}
          </MessageCard>
        )}
        {session.status === 'configuration-error' && (
          <MessageCard
            title={t('Cloud configuration is incomplete')}
            tone="error"
          >
            <p>
              {t(
                'The SaaS connection is unavailable until all public environment variables are set.',
              )}
            </p>
            <LocalFirstNotice />
            {localBudgetId !== null && <ContinueLocallyButton />}
          </MessageCard>
        )}
        {session.status === 'restoring' && (
          <MessageCard title={t('Loading cloud session...')} />
        )}
        {session.status === 'error' && (
          <MessageCard title={t('Cloud connection error')} tone="error">
            <p>
              {t(
                'The cloud API returned an error. Your local financial data is still available.',
              )}
            </p>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Button onPress={() => void session.retry()}>
                <Trans ns={CLOUD_NAMESPACE}>Retry</Trans>
              </Button>
              {localBudgetId !== null && <ContinueLocallyButton />}
            </View>
          </MessageCard>
        )}
        {session.status === 'signed-out' && (
          <MessageCard title={t('Sign in')}>
            <LocalFirstNotice />
            <ButtonWithLoading
              variant="primary"
              isLoading={session.isBusy}
              isDisabled={session.isBusy}
              onPress={() => void session.beginLogin()}
            >
              {session.isBusy ? (
                <Trans ns={CLOUD_NAMESPACE}>Signing in...</Trans>
              ) : (
                <Trans ns={CLOUD_NAMESPACE}>Sign in</Trans>
              )}
            </ButtonWithLoading>
            {localBudgetId !== null && <ContinueLocallyButton />}
          </MessageCard>
        )}
        {session.status === 'authenticated' &&
          session.api !== null &&
          session.principal !== null && (
            <AuthenticatedCloud
              api={session.api}
              principal={session.principal}
              isEntryFlow={isEntryFlow}
              isOffline={session.isOffline}
              isLoggingOut={session.isBusy}
              isEndToEndEncrypted={isEndToEndEncrypted}
              localBudgetId={localBudgetId}
              localBudgetName={localBudgetName}
              onLogout={session.logout}
              onRevalidate={session.retry}
            />
          )}
        {canSelectLocalBudget && <LocalBudgetEntry />}
      </main>
    </Page>
  );
}

function AuthenticatedCloud({
  api,
  principal,
  isEntryFlow,
  isOffline,
  isLoggingOut,
  isEndToEndEncrypted,
  localBudgetId,
  localBudgetName,
  onLogout,
  onRevalidate,
}: Readonly<{
  api: NonNullable<ReturnType<typeof useCloudSession>['api']>;
  principal: NonNullable<ReturnType<typeof useCloudSession>['principal']>;
  isEntryFlow: boolean;
  isOffline: boolean;
  isLoggingOut: boolean;
  isEndToEndEncrypted: boolean;
  localBudgetId: string | null;
  localBudgetName: string | null;
  onLogout: () => Promise<void>;
  onRevalidate: () => Promise<void>;
}>) {
  const { t } = useTranslation(CLOUD_NAMESPACE);
  const workspaces = useCloudWorkspaces(api, principal.subject, isOffline);
  const budgetBinding = useCloudBudgetBinding(
    api,
    principal.subject,
    workspaces.selectedWorkspaceId,
    localBudgetId,
    localBudgetName,
    isOffline,
  );
  const [workspaceName, setWorkspaceName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  async function submitWorkspace(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreateError(null);
    setIsCreating(true);
    try {
      await workspaces.createWorkspace(workspaceName);
      setWorkspaceName('');
    } catch {
      setCreateError(
        t(
          'The cloud API returned an error. Your local financial data is still available.',
        ),
      );
    } finally {
      setIsCreating(false);
    }
  }

  const identity = principal.email ?? principal.subject;
  return (
    <View style={{ gap: 12 }}>
      <Card style={{ margin: 0 }}>
        <View style={{ padding: 18, gap: 12 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <View>
              <h2 style={{ margin: 0, fontSize: 18 }}>
                <Trans ns={CLOUD_NAMESPACE}>Cloud account</Trans>
              </h2>
              <p style={subduedStyle}>
                {t('Signed in as {{email}}', { email: identity })}
              </p>
            </View>
            <ButtonWithLoading
              isLoading={isLoggingOut}
              isDisabled={isLoggingOut}
              onPress={() => void onLogout()}
            >
              <Trans ns={CLOUD_NAMESPACE}>Log out</Trans>
            </ButtonWithLoading>
          </View>
          {isOffline && (
            <output style={offlineStyle}>
              <strong>
                <Trans ns={CLOUD_NAMESPACE}>Offline</Trans>
              </strong>{' '}
              {t(
                'You are offline. Cloud changes are paused, but local finance continues to work.',
              )}
            </output>
          )}
        </View>
      </Card>

      <Card style={{ margin: 0 }}>
        <View style={{ padding: 18, gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>
            <Trans ns={CLOUD_NAMESPACE}>Workspaces</Trans>
          </h2>
          {workspaces.status === 'loading' && (
            <output style={subduedStyle}>{t('Loading workspaces...')}</output>
          )}
          {workspaces.status === 'error' && (
            <View style={{ gap: 8 }}>
              <p role="alert" style={errorStyle}>
                {t(
                  'The cloud API returned an error. Your local financial data is still available.',
                )}
              </p>
              <Button
                onPress={() =>
                  void (workspaces.error instanceof CloudApiError &&
                  workspaces.error.kind === 'unauthorized'
                    ? onRevalidate()
                    : workspaces.reload())
                }
              >
                <Trans ns={CLOUD_NAMESPACE}>Retry</Trans>
              </Button>
            </View>
          )}
          {workspaces.status === 'ready' && workspaces.items.length === 0 && (
            <p style={subduedStyle}>
              <Trans ns={CLOUD_NAMESPACE}>No workspace yet</Trans>
            </p>
          )}
          {workspaces.items.length > 0 && (
            <fieldset
              disabled={isOffline}
              style={{ border: 0, padding: 0, margin: 0 }}
            >
              <legend style={{ fontWeight: 600, marginBottom: 8 }}>
                <Trans ns={CLOUD_NAMESPACE}>Select workspace</Trans>
              </legend>
              <View style={{ gap: 8 }}>
                {workspaces.items.map(workspace => (
                  <label key={workspace.id} style={workspaceOptionStyle}>
                    <input
                      type="radio"
                      name="cloud-workspace"
                      value={workspace.id}
                      checked={workspaces.selectedWorkspaceId === workspace.id}
                      onChange={() => workspaces.selectWorkspace(workspace.id)}
                    />
                    <span>{workspace.name}</span>
                    {workspaces.selectedWorkspaceId === workspace.id && (
                      <strong style={{ marginLeft: 'auto' }}>
                        <Trans ns={CLOUD_NAMESPACE}>Active</Trans>
                      </strong>
                    )}
                  </label>
                ))}
              </View>
            </fieldset>
          )}

          {isOffline ? (
            <p style={subduedStyle}>
              {t(
                'Reconnect to create or refresh workspaces. Cached selection is shown read-only.',
              )}
            </p>
          ) : (
            <form
              onSubmit={event => void submitWorkspace(event)}
              style={formStyle}
            >
              <label htmlFor="cloud-workspace-name" style={{ fontWeight: 600 }}>
                <Trans ns={CLOUD_NAMESPACE}>Workspace name</Trans>
              </label>
              <input
                id="cloud-workspace-name"
                value={workspaceName}
                maxLength={120}
                required
                onChange={event => setWorkspaceName(event.target.value)}
                style={inputStyle}
              />
              {createError !== null && (
                <p role="alert" style={errorStyle}>
                  {createError}
                </p>
              )}
              <ButtonWithLoading
                type="submit"
                variant="primary"
                isLoading={isCreating}
                isDisabled={isCreating || workspaceName.trim() === ''}
              >
                {isCreating ? (
                  <Trans ns={CLOUD_NAMESPACE}>Creating...</Trans>
                ) : (
                  <Trans ns={CLOUD_NAMESPACE}>Create workspace</Trans>
                )}
              </ButtonWithLoading>
            </form>
          )}
        </View>
      </Card>

      {(!isEntryFlow || localBudgetId !== null) && (
        <CloudBudgetBindingCard
          binding={budgetBinding}
          workspaceId={workspaces.selectedWorkspaceId}
          localBudgetId={localBudgetId}
          isOffline={isOffline}
        />
      )}

      {(!isEntryFlow || localBudgetId !== null) && (
        <CloudBackupsCard
          key={`${workspaces.selectedWorkspaceId ?? 'none'}:${
            budgetBinding.selectedBudgetId ?? 'none'
          }`}
          api={api}
          subject={principal.subject}
          workspaceId={workspaces.selectedWorkspaceId}
          localBudgetId={localBudgetId}
          localBudgetName={localBudgetName}
          isOffline={isOffline}
          isEndToEndEncrypted={isEndToEndEncrypted}
          showBudgetBinding={false}
        />
      )}
    </View>
  );
}

function LocalBudgetEntry() {
  return (
    <Card style={{ width: '100%', maxWidth: 620, margin: '12px auto 0' }}>
      <section aria-labelledby="local-budget-entry-heading">
        <View style={{ gap: 10, padding: 18 }}>
          <h2
            id="local-budget-entry-heading"
            style={{ color: theme.pageText, fontSize: 18, margin: 0 }}
          >
            <Trans ns={CLOUD_NAMESPACE}>Local budgets</Trans>
          </h2>
          <p style={subduedStyle}>
            <Trans ns={CLOUD_NAMESPACE}>
              Select a local budget or create one to continue.
            </Trans>
          </p>
          <BudgetFileSelection
            createLabel={
              <Trans ns={CLOUD_NAMESPACE}>Create local budget</Trans>
            }
            emptyStateText={
              <Trans ns={CLOUD_NAMESPACE}>No local budgets yet</Trans>
            }
            showHeader={false}
            showImportAction={false}
            showTestAction={false}
          />
        </View>
      </section>
    </Card>
  );
}

function MessageCard({
  title,
  children,
  tone = 'normal',
}: Readonly<{
  title: string;
  children?: ReactNode;
  tone?: 'error' | 'normal';
}>) {
  return (
    <Card
      style={{
        maxWidth: 620,
        margin: '40px auto 0',
        borderColor: tone === 'error' ? theme.errorText : theme.cardBorder,
      }}
    >
      <View style={{ padding: 20, gap: 12 }}>
        <h2 style={{ color: theme.pageText, margin: 0, fontSize: 19 }}>
          {title}
        </h2>
        {children}
      </View>
    </Card>
  );
}

function LocalFirstNotice() {
  const { t } = useTranslation(CLOUD_NAMESPACE);
  return (
    <p style={subduedStyle}>
      {t(
        'Cloud features are optional. Your local budgets and Product Finance remain available on this device.',
      )}
    </p>
  );
}

function ContinueLocallyButton() {
  const navigate = useNavigate();
  return (
    <Button onPress={() => void navigate('/product-finance')}>
      <Trans ns={CLOUD_NAMESPACE}>Continue locally</Trans>
    </Button>
  );
}

const subduedStyle = {
  color: theme.pageTextSubdued,
  lineHeight: 1.45,
  margin: 0,
} as const;

const errorStyle = {
  color: theme.errorText,
  lineHeight: 1.45,
  margin: 0,
} as const;

const offlineStyle = {
  borderRadius: 6,
  background: theme.warningBackground,
  color: theme.warningText,
  lineHeight: 1.45,
  margin: 0,
  padding: 10,
} as const;

const formStyle = {
  display: 'grid',
  gap: 8,
  maxWidth: 440,
} as const;

const inputStyle = {
  minHeight: 42,
  width: '100%',
  border: `1px solid ${theme.formInputBorder}`,
  borderRadius: 5,
  background: theme.tableBackground,
  color: theme.formInputText,
  font: 'inherit',
  padding: '8px 10px',
} as const;

const workspaceOptionStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 44,
  padding: '8px 10px',
  borderRadius: 6,
  background: theme.pageBackground,
} as const;
